import { publishPan } from "../StageManualPan/pan";
import {
  applyDragToViewTransform,
  applyWheelToViewTransform,
  canStartTimelinePanDrag,
} from "../ViewTransform/transform";
import type { StageManualViewOptions } from "./view";

export type LockedWheelZoomHandler = (
  factor: number,
  pointerX: number,
  pointerY: number,
  stageW: number,
  stageH: number,
) => void;

export interface TouchPoint {
  x: number;
  y: number;
}

export interface PinchStep {
  /** How much closer to zoom: above 1 is in. */
  factor: number;
  centerX: number;
  centerY: number;
  panX: number;
  panY: number;
}

type TwoTouches = readonly [TouchPoint, TouchPoint];

const middle = ([a, b]: TwoTouches): TouchPoint => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const spread = ([a, b]: TwoTouches): number => Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));

/** What one movement of two fingers asks the view to do. */
export function pinchStep(before: TwoTouches, after: TwoTouches): PinchStep {
  const from = middle(before);
  const to = middle(after);
  return {
    factor: spread(after) / spread(before),
    centerX: to.x,
    centerY: to.y,
    panX: to.x - from.x,
    panY: to.y - from.y,
  };
}

/** Follows the fingers on the stage. Two of them zoom about their middle and, off the lock, pan. */
export function createStagePinch(options: StageManualViewOptions, stage: HTMLElement) {
  const touches = new Map<number, TouchPoint>();
  return {
    /** True when this finger starts a pinch. */
    onDown(event: PointerEvent, at: TouchPoint): boolean {
      if (event.pointerType !== "touch" || !canStartTimelinePanDrag(event.target)) {
        return false;
      }
      touches.set(event.pointerId, at);
      if (touches.size !== 2) {
        return false;
      }
      // The second finger turns a drag into a pinch.
      options.dragRef.current = null;
      stage.classList.remove("timeline-stage--dragging");
      stage.setPointerCapture(event.pointerId);
      event.preventDefault();
      return true;
    },
    /** True when the move belonged to a pinch. */
    onMove(pointerId: number, at: TouchPoint, size: { w: number; h: number }): boolean {
      if (touches.size !== 2 || !touches.has(pointerId)) {
        return false;
      }
      const [firstId, secondId] = [...touches.keys()] as [number, number];
      const before = [touches.get(firstId)!, touches.get(secondId)!] as const;
      touches.set(pointerId, at);
      const step = pinchStep(before, [touches.get(firstId)!, touches.get(secondId)!]);
      if (options.getCameraLock()) {
        options.onLockedWheelZoom?.(step.factor, step.centerX, step.centerY, size.w, size.h);
      } else {
        const zoomed = applyWheelToViewTransform(
          options.getView(), size.w, size.h, step.centerX, step.centerY, step.factor,
        );
        publishPan(options, applyDragToViewTransform(
          zoomed, 0, 0, step.panX, step.panY, size.w, size.h,
        ));
      }
      options.onManualZoom?.();
      return true;
    },
    onDone(pointerId: number): void {
      touches.delete(pointerId);
    },
  };
}
